"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { FilterIcon } from "lucide-react";
import { BackendSelectWithLabel } from "./backend-select";
import { BackendButton } from "./backend-button";
import { BackendMultiSelect } from "./offers/selects/backend-multi-select";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/app/components/ui/drawer";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/app/components/ui/sheet";
import type {
  BackendFilterDefinitionT,
  BackendFilterStateT,
  BackendFilterValueT,
  BackendTableRowT,
} from "@/app/types/backend-table";

const normalizeValue = (value: unknown) => String(value ?? "").toLowerCase();
const STATUS_FILTER_ID = "status";
const ARCHIVED_STATUS = "archived";
const ALL_OPTION_VALUE = "__all__";
const ALL_OPTION = { value: ALL_OPTION_VALUE, label: "Alle" };

const ensureAllOption = (options: BackendFilterDefinitionT["options"]) => {
  const hasAllOption = options.some(
    (option) => option.value === ALL_OPTION_VALUE
  );
  if (hasAllOption) {
    return options;
  }
  return [ALL_OPTION, ...options];
};

const mapFilterValueToSelectValue = (value: BackendFilterValueT) => {
  if (Array.isArray(value)) {
    return ALL_OPTION_VALUE;
  }
  return value === null || value === undefined || value === ""
    ? ALL_OPTION_VALUE
    : value;
};

const mapSelectValueToFilterValue = (value: string): BackendFilterValueT => {
  if (value === ALL_OPTION_VALUE || value === "") {
    return null;
  }
  return value;
};

/**
 * Gets a nested value from an object using a dot-notation path (e.g., "primaryRelation.title")
 */
const getNestedValue = (obj: unknown, path: string): unknown => {
  const keys = path.split(".");
  let current: unknown = obj;

  for (const key of keys) {
    if (
      current &&
      typeof current === "object" &&
      key in current &&
      current !== null
    ) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }

  return current;
};

/**
 * Archived rows are hidden from backend lists unless the status filter
 * explicitly includes "archived". The same rule is applied when building the
 * available dropdown options, so archived items never show up as filter
 * values while they are not part of the visible table data.
 */
export const excludeArchivedRowsUnlessSelected = (
  data: BackendTableRowT[],
  statusFilterValue: BackendFilterValueT
): BackendTableRowT[] => {
  const hasArchivedSelected =
    Array.isArray(statusFilterValue) &&
    statusFilterValue.includes(ARCHIVED_STATUS);

  if (hasArchivedSelected) {
    return data;
  }

  return data.filter((row) => row.status !== ARCHIVED_STATUS);
};

const getInitialFilterState = (
  filters?: BackendFilterDefinitionT[]
): BackendFilterStateT => {
  if (!filters) return {};

  return filters.reduce<BackendFilterStateT>((acc, filter) => {
    acc[filter.id] = filter.initialValue ?? null;
    return acc;
  }, {});
};

const areFilterValuesEqual = (
  previousValue: BackendFilterValueT,
  nextValue: BackendFilterValueT
) => {
  if (Array.isArray(previousValue) || Array.isArray(nextValue)) {
    if (!Array.isArray(previousValue) || !Array.isArray(nextValue)) {
      return false;
    }

    if (previousValue.length !== nextValue.length) {
      return false;
    }

    return previousValue.every((value, index) => value === nextValue[index]);
  }

  return previousValue === nextValue;
};

const areFilterStatesEqual = (
  previousState: BackendFilterStateT,
  nextState: BackendFilterStateT
) => {
  const previousKeys = Object.keys(previousState);
  const nextKeys = Object.keys(nextState);

  if (previousKeys.length !== nextKeys.length) {
    return false;
  }

  return nextKeys.every((key) =>
    areFilterValuesEqual(previousState[key], nextState[key])
  );
};

const matchesFilter = (
  filter: BackendFilterDefinitionT,
  row: BackendTableRowT,
  value: BackendFilterValueT
): boolean => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return true;
  }

  if (filter.predicate) {
    return filter.predicate(row, value);
  }

  const fieldKey = filter.field ?? filter.id;
  // Support nested paths (e.g., "primaryRelation.title")
  const rowValue = fieldKey.includes(".")
    ? getNestedValue(row, fieldKey)
    : row[fieldKey];

  if (filter.type === "multi-select") {
    if (!Array.isArray(value)) return true;

    const normalizedSelected = value.map(normalizeValue);
    const normalizedRowValues = Array.isArray(rowValue)
      ? rowValue.map(normalizeValue)
      : String(rowValue ?? "")
          .split(",")
          .map((part) => normalizeValue(part.trim()))
          .filter(Boolean);

    if (normalizedRowValues.length === 0) return false;

    return normalizedSelected.some((selected) =>
      normalizedRowValues.some(
        (rowPart) => rowPart === selected || rowPart.includes(selected)
      )
    );
  }

  return normalizeValue(rowValue) === normalizeValue(value);
};

const filterDataExcludingFilter = (
  data: BackendTableRowT[],
  filters: BackendFilterDefinitionT[],
  filterValues: BackendFilterStateT,
  excludedFilterId: string
) =>
  data.filter((row) =>
    filters.every((filter) => {
      if (filter.id === excludedFilterId) return true;
      return matchesFilter(filter, row, filterValues[filter.id]);
    })
  );

const buildAvailableOptions = (
  filter: BackendFilterDefinitionT,
  data: BackendTableRowT[]
) => {
  if (filter.preserveOptions) {
    return filter.options;
  }

  return filter.options.filter((option) => {
    const testValue =
      filter.type === "multi-select" ? [option.value] : option.value;
    return data.some((row) => matchesFilter(filter, row, testValue));
  });
};

const mergeSelectedOptions = (
  filter: BackendFilterDefinitionT,
  options: BackendFilterDefinitionT["options"],
  selectedValue: BackendFilterValueT
) => {
  const selectedValues = Array.isArray(selectedValue)
    ? selectedValue
    : selectedValue
      ? [selectedValue]
      : [];

  if (selectedValues.length === 0) {
    return options;
  }

  const optionMap = new Map(
    filter.options.map((option) => [option.value, option])
  );
  const merged = [...options];
  const existingValues = new Set(options.map((option) => option.value));

  selectedValues.forEach((value) => {
    if (!existingValues.has(value)) {
      const fallbackOption = optionMap.get(value) || { value, label: value };
      merged.push(fallbackOption);
    }
  });

  return merged;
};

export function useBackendFilters(
  data: BackendTableRowT[],
  filters?: BackendFilterDefinitionT[]
) {
  const [filterValues, setFilterValues] = useState<BackendFilterStateT>(() =>
    getInitialFilterState(filters)
  );

  useEffect(() => {
    const nextFilterState = getInitialFilterState(filters);

    setFilterValues((previousState) =>
      areFilterStatesEqual(previousState, nextFilterState)
        ? previousState
        : nextFilterState
    );
  }, [filters]);

  const setFilterValue = useCallback(
    (id: string, value: BackendFilterValueT) => {
      setFilterValues((prev) => ({ ...prev, [id]: value }));
    },
    []
  );

  const clearFilter = useCallback((id: string) => {
    setFilterValues((prev) => ({ ...prev, [id]: null }));
  }, []);

  const clearAllFilters = useCallback(() => {
    const nextFilterState = getInitialFilterState(filters);

    setFilterValues((previousState) =>
      areFilterStatesEqual(previousState, nextFilterState)
        ? previousState
        : nextFilterState
    );
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    return Object.values(filterValues).filter((value) => {
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    }).length;
  }, [filterValues]);

  const filteredData = useMemo(() => {
    if (!filters || filters.length === 0) return data;

    return data.filter((row) =>
      filters.every((filter) =>
        matchesFilter(filter, row, filterValues[filter.id])
      )
    );
  }, [data, filters, filterValues]);

  const filtersWithOptions = useMemo(() => {
    if (!filters || filters.length === 0) return [];

    // Dropdown options must only reflect rows that are actually visible,
    // i.e. archived rows are ignored unless "archived" is selected. The status
    // filter itself keeps the full data so "Archiviert" stays selectable.
    const nonArchivedData = excludeArchivedRowsUnlessSelected(
      data,
      filterValues[STATUS_FILTER_ID]
    );

    return filters.map((filter) => {
      const optionBaseData =
        filter.id === STATUS_FILTER_ID ? data : nonArchivedData;
      const dataWithoutCurrent = filterDataExcludingFilter(
        optionBaseData,
        filters,
        filterValues,
        filter.id
      );
      const availableOptions = buildAvailableOptions(
        filter,
        dataWithoutCurrent
      );
      const mergedOptions = mergeSelectedOptions(
        filter,
        availableOptions,
        filterValues[filter.id]
      );

      return {
        ...filter,
        options: mergedOptions,
      };
    });
  }, [data, filters, filterValues]);

  return {
    filteredData,
    filterValues,
    setFilterValue,
    clearFilter,
    clearAllFilters,
    activeFilterCount,
    filtersWithOptions,
  };
}

type BackendFilterBarPropsT = {
  filters: BackendFilterDefinitionT[];
  values: BackendFilterStateT;
  onChange: (id: string, value: BackendFilterValueT) => void;
  onClearAll: () => void;
  className?: string;
  activeFilterCount?: number;
};

type BackendFilterControlPropsT = {
  filter: BackendFilterDefinitionT;
  value: BackendFilterValueT;
  onChange: (id: string, value: BackendFilterValueT) => void;
  showLabel?: boolean;
};

function BackendFilterControl({
  filter,
  value,
  onChange,
  showLabel = false,
}: BackendFilterControlPropsT) {
  if (filter.type === "select") {
    const optionsWithAll = ensureAllOption(filter.options);
    const selectedValue = mapFilterValueToSelectValue(value as string | null);

    return (
      <BackendSelectWithLabel
        label={showLabel ? filter.label : undefined}
        options={optionsWithAll}
        value={selectedValue}
        placeholder={filter.placeholder || filter.label}
        onChange={(selectedValue) =>
          onChange(filter.id, mapSelectValueToFilterValue(selectedValue))
        }
        className="h-10"
      />
    );
  }

  if (filter.type === "multi-select") {
    return (
      <BackendMultiSelect
        label={showLabel ? filter.label : undefined}
        options={filter.options}
        selectedValues={(value as string[]) || []}
        onSelectionChange={(values) => onChange(filter.id, values)}
        placeholder={filter.placeholder || filter.label}
        searchPlaceholder="Suchen..."
        className="h-10"
        disablePortal={showLabel}
      />
    );
  }

  return null;
}

export function BackendFilterBar({
  filters,
  values,
  onChange,
  className,
}: BackendFilterBarPropsT) {
  if (!filters || filters.length === 0) {
    return null;
  }

  return (
    <div className={`flex items-center gap-3 ${className || ""}`}>
      <div className="flex items-center gap-3">
        {filters.map((filter) => {
          const currentValue = values[filter.id];

          return (
            <div key={filter.id} className="w-[160px] flex-none">
              <BackendFilterControl
                filter={filter}
                value={currentValue}
                onChange={onChange}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

type BackendMobileFilterDrawerPropsT = {
  filters: BackendFilterDefinitionT[];
  values: BackendFilterStateT;
  onChange: (id: string, value: BackendFilterValueT) => void;
  onClearAll: () => void;
  activeFilterCount?: number;
};

export function BackendMobileFilterDrawer({
  filters,
  values,
  onChange,
  onClearAll,
  activeFilterCount = 0,
}: BackendMobileFilterDrawerPropsT) {
  if (!filters || filters.length === 0) {
    return null;
  }

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <BackendButton
          type="button"
          variant="secondary"
          className="h-10 rounded-[10px] px-3 font-normal"
        >
          <FilterIcon className="size-4" strokeWidth={1.5} />
          Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </BackendButton>
      </DrawerTrigger>
      <DrawerContent className="max-h-[88dvh] rounded-t-[24px] border-0 bg-[var(--c-card)] font-[var(--font-backend-sans)] text-[var(--c-text)] shadow-[0_-18px_48px_rgba(80,60,20,0.18)]">
        <DrawerHeader className="border-b border-[var(--c-border-soft-2)] px-5 pb-4 text-left">
          <DrawerTitle className="text-base font-semibold text-[var(--c-ink)]">
            Filter
          </DrawerTitle>
          <DrawerDescription className="text-sm text-[var(--c-muted)]">
            Grenzen Sie die Tabelle nach den gewünschten Kriterien ein.
          </DrawerDescription>
        </DrawerHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {filters.map((filter) => (
            <div key={filter.id} className="min-w-0">
              <BackendFilterControl
                filter={filter}
                value={values[filter.id]}
                onChange={onChange}
                showLabel
              />
            </div>
          ))}
        </div>

        <DrawerFooter className="border-t border-[var(--c-border-soft-2)] bg-[var(--c-card)] px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            {activeFilterCount > 0 ? (
              <BackendButton
                type="button"
                variant="ghost"
                onClick={onClearAll}
                className="h-10 px-0 text-[var(--c-muted)] hover:bg-transparent"
              >
                Alle löschen
              </BackendButton>
            ) : (
              <span />
            )}

            <DrawerClose asChild>
              <BackendButton type="button" className="h-10 min-w-[140px]">
                Filter anzeigen
              </BackendButton>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

type BackendDesktopFilterSheetPropsT = {
  filters: BackendFilterDefinitionT[];
  values: BackendFilterStateT;
  onChange: (id: string, value: BackendFilterValueT) => void;
  onClearAll: () => void;
  activeFilterCount?: number;
};

export function BackendDesktopFilterSheet({
  filters,
  values,
  onChange,
  onClearAll,
  activeFilterCount = 0,
}: BackendDesktopFilterSheetPropsT) {
  if (!filters || filters.length === 0) {
    return null;
  }

  return (
    <Sheet>
      <SheetTrigger asChild>
        <BackendButton
          type="button"
          variant="secondary"
          className="h-10 rounded-[10px] px-3 font-normal"
        >
          <FilterIcon className="size-4" strokeWidth={1.5} />
          Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </BackendButton>
      </SheetTrigger>
      <SheetContent
        side="right"
        size="default"
        className="flex w-[420px] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-l-[24px] border-0 bg-[var(--c-card)] p-0 font-[var(--font-backend-sans)] text-[var(--c-text)] shadow-[0_24px_80px_rgba(80,60,20,0.24)]"
      >
        <SheetHeader className="border-b border-[var(--c-border-soft-2)] px-5 py-4 text-left">
          <SheetTitle className="text-base font-semibold text-[var(--c-ink)]">
            Filter
          </SheetTitle>
          <SheetDescription className="text-sm text-[var(--c-muted)]">
            Grenzen Sie die Tabelle nach den gewünschten Kriterien ein.
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {filters.map((filter) => (
            <div key={filter.id} className="min-w-0">
              <BackendFilterControl
                filter={filter}
                value={values[filter.id]}
                onChange={onChange}
                showLabel
              />
            </div>
          ))}
        </div>

        <div className="border-t border-[var(--c-border-soft-2)] px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            {activeFilterCount > 0 ? (
              <BackendButton
                type="button"
                variant="ghost"
                onClick={onClearAll}
                className="h-10 px-0 text-[var(--c-muted)] hover:bg-transparent"
              >
                Alle löschen
              </BackendButton>
            ) : (
              <span />
            )}

            <SheetClose asChild>
              <BackendButton type="button" className="h-10 min-w-[140px]">
                Filter anzeigen
              </BackendButton>
            </SheetClose>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
